import React, { useMemo } from "react";
import ScrollToErrorForm from "src/components/scroll_to_error_form";
import { usePhoneVerifiedSubmission } from "src/customer/use_phone_verified_submission";
import { continueWithoutAccount } from "src/customer/create_account";
import VerificationCodeForm from "src/customer/verification_code_form";

type GuestDetails = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
};

const GuestCheckout = ({
  afterGuestCheckoutCallback,
  initialDetails = {},
}: {
  afterGuestCheckoutCallback: () => void;
  initialDetails?: GuestDetails;
}) => {
  const { details, submittedValue, verification } = usePhoneVerifiedSubmission({ submit: continueWithoutAccount, onSuccess: afterGuestCheckoutCallback });

  return (
    <>
      <GuestCheckoutForm
        initialFirstName={submittedValue("first_name") ?? initialDetails.firstName}
        initialLastName={submittedValue("last_name") ?? initialDetails.lastName}
        initialEmail={submittedValue("email") ?? initialDetails.email}
        initialPhoneNumber={submittedValue("phone_number") ?? initialDetails.phoneNumber}
        {...details}

      />
      {verification && <VerificationCodeForm verification={verification} isModal={true} />}
    </>
  );
};

const GuestCheckoutForm = ({
  errors,
  isSubmitting,
  onSubmit,
  clearErrors,
  initialFirstName,
  initialLastName,
  initialEmail,
  initialPhoneNumber,
}: {
  errors: Record<string, string[]>;
  isSubmitting: boolean;
  onSubmit: (formData: FormData) => void;
  clearErrors: () => void;
  initialFirstName?: string;
  initialLastName?: string;
  initialEmail?: string;
  initialPhoneNumber?: string;
}) => {
  const [firstName, setFirstName] = React.useState(initialFirstName ?? "");
  const [lastName, setLastName] = React.useState(initialLastName ?? "");
  const [email, setEmail] = React.useState(initialEmail ?? "");
  const [phoneNumber, setPhoneNumber] = React.useState(initialPhoneNumber ?? "");
  const navHeaderRef = React.useRef<HTMLElement | null>(document.querySelector<HTMLElement>("header.is-sticky"));

  const submitSignUp = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(new FormData(e.currentTarget));
  }

  const fieldErrors = (errorKey: string) => {
    const fieldErrorMessages = errors[errorKey] || [];

    if (fieldErrorMessages.length > 0) {
      return (
        <div className="error" data-testid="fieldError">
          {fieldErrorMessages.join(". ")}
        </div>
      );
    } else {
      return null;
    }
  }

  const hasAnyErrors = useMemo(() => Object.values(errors).some((errors) => errors.length > 0), [errors])

  return (
    <>
      <h1>Guest checkout</h1>
      <p>Don&#39;t need an account? No worries - just continue without one.</p>

      <ScrollToErrorForm errors={errors} stickyHeaderRef={navHeaderRef} onSubmit={submitSignUp}>
        <fieldset className={hasAnyErrors ? "has-error" : undefined}>
          <div className="text-input has-max-width" data-cy="guest-first-name-input">
            <label htmlFor="first_name">First name</label>
            <input
              id="first_name"
              type="text"
              name="first_name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            {fieldErrors("first_name")}
          </div>

          <div className="text-input has-max-width" data-cy="guest-last-name-input">
            <label htmlFor="last_name">Last name</label>
            <input
              id="last_name"
              type="text"
              name="last_name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
            {fieldErrors("last_name")}
          </div>

          <div className="text-input has-max-width" data-cy="guest-email-input">
            <label htmlFor="guest_email">Guest email</label>
            <input
              id="guest_email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {fieldErrors("email")}
          </div>

          <div className="text-input has-max-width" data-cy="guest-phone-number-input">
            <label htmlFor="phone_number">Phone number</label>
            <input
              id="phone_number"
              type="tel"
              name="phone_number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              pattern="[+\-\(\)0-9 ]+"
              title="Please enter a valid phone number"
              required
            />
            {fieldErrors("phone_number")}
          </div>

          {fieldErrors("base")}
        </fieldset>

        <button
          type="submit"
          className="button"
          disabled={isSubmitting}
          onClick={clearErrors}
          data-cy="guest-proceed-button">Continue as guest
        </button>
      </ScrollToErrorForm>
    </>
  );
};

export default GuestCheckout;
